import type { CSSProperties } from "react";
import logoMark from "@assets/logo-mark.png";

type Props = {
  /** Hauteur du logo en px. La largeur suit le ratio du dessin. */
  height?: number;
  style?: CSSProperties;
  className?: string;
};

// Ratio du fichier recadré (1179×962). Figé ici pour que la largeur suive la
// hauteur sans déformer le dessin, quel que soit l'endroit où le logo est posé.
const RATIO = 1179 / 962;

/**
 * Logo Alomain — le « A » traversé par la main et l'oiseau.
 *
 * ## Le fichier utilisé
 * `assets/logo-mark.png` : recadrage au pixel de `alomain.png` sur la boîte du
 * dessin. L'original mesure 3508×2481 avec des marges vides très asymétriques
 * (41 % sous le logo contre 18 % au-dessus) : affiché tel quel, le logo aurait
 * paru poussé vers le haut de son cadre et anormalement petit. Le recadrage
 * conserve le canal alpha (RGBA) — le fond est réellement transparent, il n'y a
 * pas de rectangle blanc.
 *
 * `object-fit: contain` remplace `resizeMode="contain"` : aucun rognage si le
 * conteneur impose un autre ratio.
 *
 * ## Web
 * `require()` devient un import ES : Vite le résout au build, empreinte le nom
 * de fichier pour le cache, et signale une erreur si l'asset manque — là où le
 * `require` natif échouait à l'exécution.
 *
 * `width` et `height` sont posés en attributs EN PLUS du style : ils donnent au
 * navigateur le ratio avant le chargement de l'image et évitent le décalage de
 * mise en page (CLS) qui n'existait pas en natif.
 */
export function AlomainLogo({ height = 30, style, className }: Props) {
  const width = Math.round(height * RATIO);

  return (
    <img
      src={logoMark}
      alt="Alomain"
      width={width}
      height={height}
      className={className}
      style={{ width, height, objectFit: "contain", ...style }}
    />
  );
}
